import React, { useState, useEffect } from 'react';
import dataManager from '../utils/dataManager';
import {
  User,
  Settings,
  LogIn,
  LogOut,
  Cloud,
  UploadCloud,
  Download,
  Upload,
  Volume2,
  Music,
  Shield,
  Bell,
  HardDrive,
  Wifi,
  Crown,
  Info,
  Check,
  X
} from 'lucide-react';

const SettingsPage = () => {
  // 模拟登录状态
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  
  // 设置状态
  const [settings, setSettings] = useState({
    // 播放设置
    crossfade: false,
    highQuality: true,
    volume: 80,
    

    
    // 界面设置
    lyricsFontSize: 'medium',
    
    // 隐私设置
    shareListening: false,
    analytics: true,
    
    // 通知设置
    desktopNotifications: true,
    soundNotifications: false
  });

  const [showToast, setShowToast] = useState(null);

  // 加载用户信息和设置
  useEffect(() => {
    const savedLoginState = localStorage.getItem('tunefree_login_state');
    const savedUserInfo = localStorage.getItem('tunefree_user_info');
    const savedSettings = localStorage.getItem('tunefree_settings');
    
    if (savedLoginState === 'true' && savedUserInfo) {
      setIsLoggedIn(true);
      setUserInfo(JSON.parse(savedUserInfo));
    }
    
    const loadedSettings = dataManager.getSettings();
    setSettings(loadedSettings);
  }, []);

  // 保存设置
  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    dataManager.saveSettings(newSettings);
    showToastMessage('设置已保存', 'success');
  };

  // 显示提示消息
  const showToastMessage = (message, type = 'success') => {
    setShowToast({ message, type });
    setTimeout(() => setShowToast(null), 3000);
  };

  // 登录功能
  const handleLogin = (email, password) => {
    // 模拟登录API
    setTimeout(() => {
      const userData = {
        id: '123456',
        email: email,
        username: email.split('@')[0],
        isPremium: Math.random() > 0.5, // 随机分配高级用户
        joinDate: '2024-01-15'
      };
      
      setIsLoggedIn(true);
      setUserInfo(userData);
      localStorage.setItem('tunefree_login_state', 'true');
      localStorage.setItem('tunefree_user_info', JSON.stringify(userData));
      showToastMessage('登录成功！');
    }, 1000);
  };

  // 登出功能
  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserInfo(null);
    localStorage.removeItem('tunefree_login_state');
    localStorage.removeItem('tunefree_user_info');
    showToastMessage('已退出登录');
  };

  // 导出数据
  const handleExportData = () => {
    try {
      const exportData = dataManager.exportData();
      const dataBlob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tunefree-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToastMessage('数据导出成功！');
    } catch (error) {
      console.error('导出数据失败:', error);
      showToastMessage('导出数据失败，请重试');
    }
  };

  // 导入数据
  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        const success = dataManager.importData(importData);
        
        if (success) {
          showToastMessage('数据导入成功！页面将刷新以应用更改');
          // 延迟刷新页面以让用户看到成功消息
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          showToastMessage('导入数据失败，请检查文件格式');
        }
      } catch (error) {
        console.error('导入数据失败:', error);
        showToastMessage('导入数据失败，请检查文件格式');
      }
    };
    
    reader.readAsText(file);
    // 重置文件输入，允许重复选择同一文件
    event.target.value = '';
  };

  // 数据同步功能
  const handleDataSync = () => {
    if (!userInfo?.isPremium) {
      showToastMessage('该功能仅限高级用户使用', 'error');
      return;
    }
    
    showToastMessage('正在同步数据...', 'info');
    // 模拟同步过程
    setTimeout(() => {
      showToastMessage('数据同步完成！');
    }, 2000);
  };

  // 设置项组件
  const SettingItem = ({ icon: Icon, title, description, children, isPremium = false }) => (
    <div className="flex items-center justify-between p-4 hover:bg-dark-200 rounded-xl transition-colors duration-200">
      <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-dark-200 rounded-xl flex items-center justify-center">
        <Icon size={20} className="text-dark-600" />
      </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-dark-800">{title}</h3>
            {isPremium && (
              <Crown size={14} className="text-yellow-500" title="高级用户专属" />
            )}
          </div>
          {description && (
            <p className="text-sm text-dark-500 mt-1">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {children}
      </div>
    </div>
  );

  // 切换开关组件
  const Toggle = ({ checked, onChange, disabled = false }) => (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
        checked ? 'bg-primary-500' : 'bg-dark-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div
        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-0.5'
        }`}
      />
    </button>
  );

  // 登录对话框
  const LoginDialog = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!email || !password) return;
      
      setIsLoading(true);
      handleLogin(email, password);
      setTimeout(() => {
        setIsLoading(false);
        setShowLoginDialog(false);
      }, 1000);
    };

    if (!showLoginDialog) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-dark-100 rounded-2xl shadow-2xl w-full max-w-md border border-dark-200">
          <div className="p-6 border-b border-dark-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-dark-800">登录账户</h3>
              <button
                onClick={() => setShowLoginDialog(false)}
                className="p-2 hover:bg-dark-100 rounded-lg transition-colors duration-200"
              >
                <X size={20} className="text-dark-600" />
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-2">
                  邮箱地址
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入邮箱地址"
                  className="w-full px-4 py-3 bg-dark-100 border border-dark-200 rounded-xl text-dark-800 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all duration-200"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-2">
                  密码
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full px-4 py-3 bg-dark-100 border border-dark-200 rounded-xl text-dark-800 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all duration-200"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowLoginDialog(false)}
                className="flex-1 px-4 py-3 bg-dark-300 text-dark-700 rounded-xl font-medium hover:bg-dark-400 transition-colors duration-200"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={!email || !password || isLoading}
                className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? '登录中...' : '登录'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 bg-dark-50 overflow-y-auto scrollbar-thin">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-dark-800 mb-2">设置</h1>
          <p className="text-dark-500">管理您的账户和应用偏好设置</p>
        </div>

        {/* 账户部分 */}
        <div className="bg-dark-100 rounded-2xl border border-dark-200 mb-6">
          <div className="p-6 border-b border-dark-200">
            <h2 className="text-lg font-semibold text-dark-800 flex items-center gap-2">
              <User size={20} />
              账户管理
            </h2>
          </div>
          
          <div className="p-6">
            {isLoggedIn ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-dark-200 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">
                      {userInfo?.username?.[0]?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-dark-800">{userInfo?.username}</h3>
                      {userInfo?.isPremium && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1">
                          <Crown size={12} />
                          高级用户
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-dark-500">{userInfo?.email}</p>
                  </div>
                </div>
                
                <SettingItem
                  icon={Download}
                  title="导出数据"
                  description="下载您的所有歌单、收藏和设置到本地文件"
                >
                  <button
                    onClick={handleExportData}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors duration-200"
                  >
                    导出数据
                  </button>
                </SettingItem>
                
                <SettingItem
                  icon={Upload}
                  title="导入数据"
                  description="从本地备份文件恢复您的数据"
                >
                  <label className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors duration-200 cursor-pointer">
                    选择文件
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportData}
                      className="hidden"
                    />
                  </label>
                </SettingItem>

                <SettingItem
                  icon={UploadCloud}
                  title="同步数据至服务器"
                  description="将您的歌单、收藏和设置同步到云端"
                  isPremium={true}
                >
                  <button
                    onClick={handleDataSync}
                    disabled={!userInfo?.isPremium}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                      userInfo?.isPremium
                        ? 'bg-primary-500 text-white hover:bg-primary-600'
                        : 'bg-dark-200 text-dark-400 cursor-not-allowed'
                    }`}
                  >
                    立即同步
                  </button>
                </SettingItem>
                
                <SettingItem
                  icon={LogOut}
                  title="退出登录"
                  description="退出当前账户"
                >
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors duration-200"
                  >
                    退出登录
                  </button>
                </SettingItem>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-dark-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User size={24} className="text-dark-400" />
                  </div>
                  <h3 className="font-medium text-dark-800 mb-2">尚未登录</h3>
                  <p className="text-dark-500 mb-4">登录后可享受更多功能</p>
                  <button
                    onClick={() => setShowLoginDialog(true)}
                    className="px-6 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors duration-200"
                  >
                    立即登录
                  </button>
                </div>
                
                {/* 本地数据管理 */}
                <div className="border-t border-dark-200 pt-4">
                  <SettingItem
                    icon={Download}
                    title="导出数据"
                    description="下载您的所有歌单、收藏和设置到本地文件"
                  >
                    <button
                      onClick={handleExportData}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors duration-200"
                    >
                      导出数据
                    </button>
                  </SettingItem>
                  
                  <SettingItem
                    icon={Upload}
                    title="导入数据"
                    description="从本地备份文件恢复您的数据"
                  >
                    <label className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors duration-200 cursor-pointer">
                      选择文件
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImportData}
                        className="hidden"
                      />
                    </label>
                  </SettingItem>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 播放设置 */}
        <div className="bg-dark-100 rounded-2xl border border-dark-200 mb-6">
          <div className="p-6 border-b border-dark-200">
            <h2 className="text-lg font-semibold text-dark-800 flex items-center gap-2">
              <Volume2 size={20} />
              播放设置
            </h2>
          </div>
          
          <div className="divide-y divide-dark-200">
            <SettingItem
              icon={Wifi}
              title="淡入淡出"
              description="歌曲切换时使用淡入淡出效果"
            >
              <Toggle
                checked={settings.crossfade}
                onChange={(checked) => saveSettings({ ...settings, crossfade: checked })}
              />
            </SettingItem>
            
            <SettingItem
              icon={HardDrive}
              title="高音质播放"
              description="使用更高质量的音频流"
            >
              <Toggle
                checked={settings.highQuality}
                onChange={(checked) => saveSettings({ ...settings, highQuality: checked })}
              />
            </SettingItem>
          </div>
        </div>



        {/* 界面设置 */}
        <div className="bg-dark-100 rounded-2xl border border-dark-200 mb-6">
          <div className="p-6 border-b border-dark-200">
            <h2 className="text-lg font-semibold text-dark-800 flex items-center gap-2">
              <Settings size={20} />
              界面设置
            </h2>
          </div>
          
          <div className="divide-y divide-dark-200">
            <SettingItem
              icon={Music}
              title="播放页面歌词大小"
              description="调整播放页面歌词的字体大小"
            >
              <select
                value={settings.lyricsFontSize}
                onChange={(e) => saveSettings({ ...settings, lyricsFontSize: e.target.value })}
                className="px-3 py-2 bg-dark-200 border border-dark-300 rounded-lg text-dark-800 focus:outline-none focus:ring-2 focus:ring-primary-400/50"
              >
                <option value="small">小</option>
                <option value="medium">中</option>
                <option value="large">大</option>
                <option value="xlarge">特大</option>
              </select>
            </SettingItem>
          </div>
        </div>

        {/* 隐私设置 */}
        <div className="bg-dark-100 rounded-2xl border border-dark-200 mb-6">
          <div className="p-6 border-b border-dark-200">
            <h2 className="text-lg font-semibold text-dark-800 flex items-center gap-2">
              <Shield size={20} />
              隐私设置
            </h2>
          </div>
          
          <div className="divide-y divide-dark-200">
            <SettingItem
              icon={Bell}
              title="桌面通知"
              description="显示新歌曲和播放状态通知"
            >
              <Toggle
                checked={settings.desktopNotifications}
                onChange={(checked) => saveSettings({ ...settings, desktopNotifications: checked })}
              />
            </SettingItem>
            
            <SettingItem
              icon={Cloud}
              title="使用情况分析"
              description="帮助改进应用体验"
            >
              <Toggle
                checked={settings.analytics}
                onChange={(checked) => saveSettings({ ...settings, analytics: checked })}
              />
            </SettingItem>
          </div>
        </div>

        {/* 关于 */}
        <div className="bg-dark-100 rounded-2xl border border-dark-200">
          <div className="p-6 border-b border-dark-200">
            <h2 className="text-lg font-semibold text-dark-800 flex items-center gap-2">
              <Info size={20} />
              关于应用
            </h2>
          </div>
          
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <img src="/logo.png" alt="TuneFree" className="w-12 h-12" />
              <div>
                <h3 className="font-semibold text-dark-800">TuneFree Next</h3>
                <p className="text-sm text-dark-500">版本 内测 1.0.0</p>
              </div>
            </div>
            <p className="text-dark-600 text-sm mb-4">
              TuneFree Next！船新版本，扬帆启航！
            </p>
            <div className="space-y-2 text-sm text-dark-500">
              <p>© 2025 TuneFree Next. 保留所有权利。</p>
              <div className="flex gap-4">
                <button className="text-primary-500 hover:text-primary-600">隐私政策</button>
                <button className="text-primary-500 hover:text-primary-600">服务条款</button>
                <button className="text-primary-500 hover:text-primary-600">意见反馈</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 登录对话框 */}
      <LoginDialog />

      {/* 提示消息 */}
      {showToast && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className={`px-6 py-3 rounded-2xl shadow-lg backdrop-blur-md flex items-center gap-2 ${
            showToast.type === 'success' ? 'bg-green-500/90 text-white' :
            showToast.type === 'error' ? 'bg-red-500/90 text-white' :
            'bg-blue-500/90 text-white'
          }`}>
            {showToast.type === 'success' && <Check size={16} />}
            {showToast.type === 'error' && <X size={16} />}
            <p className="font-medium">{showToast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
